const ACCESS_TOKEN_KEY = "access_token";

/**
 * Decode a JWT token and extract the payload
 */
const decodeJwt = (token: string): { exp?: number } | null => {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

/**
 * Check if a token is expired (with 5-minute buffer)
 */
const isTokenExpired = (token: string): boolean => {
  const payload = decodeJwt(token);
  if (!payload?.exp) return true;

  const bufferSeconds = 5 * 60;
  const nowSeconds = Math.floor(Date.now() / 1000);

  return payload.exp <= nowSeconds + bufferSeconds;
};

const tryWriteLocal = (token: string): boolean => {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
    return true;
  } catch {
    return false;
  }
};

const writeSession = (token: string) => {
  try {
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  } catch {
    // silent fail
  }
};

export const setCachedAccessToken = (token: string) => {
  if (tryWriteLocal(token)) return;
  writeSession(token);
};

const promoteSessionToken = (token: string | null) => {
  if (!token) return;
  if (tryWriteLocal(token)) {
    try {
      sessionStorage.removeItem(ACCESS_TOKEN_KEY);
    } catch {
      // Ignore cleanup failure
    }
  }
};

export const getCachedAccessToken = (): string | null => {
  try {
    const localToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (localToken) {
      if (isTokenExpired(localToken)) {
        clearCachedAccessToken();
        return null;
      }
      return localToken;
    }
    const sessionToken = sessionStorage.getItem(ACCESS_TOKEN_KEY);
    if (sessionToken) {
      if (isTokenExpired(sessionToken)) {
        clearCachedAccessToken();
        return null;
      }
      promoteSessionToken(sessionToken);
    }
    return sessionToken;
  } catch {
    return null;
  }
};

export const clearCachedAccessToken = () => {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    // ignore
  }
};
